import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, RotateCcw, Scale, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA = process.env.REACT_APP_WHATSAPP_NUMBER;

const complaints = ["Falta de energia", "Não ganho massa", "Sono ruim", "Dores e articulações", "Imunidade baixa", "Falta de foco"];
const frequencies = ["1–2x", "3–4x", "5x ou mais"];

const renderMd = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? (
      <strong key={i} className="text-military font-semibold">{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );

export const DosageCalculator = () => {
  const [complaint, setComplaint] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [weight, setWeight] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const ready = complaint && frequency && Number(weight) >= 30 && Number(weight) <= 250;

  const run = async () => {
    setLoading(true);
    setText("");
    try {
      const res = await fetch(`${API}/dosage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ complaint, frequency, weight: Number(weight) }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") break;
          try {
            const { t } = JSON.parse(payload);
            if (t) setText((prev) => prev + t);
          } catch {}
        }
      }
    } catch {
      setText("Não consegui calcular agora. Tente novamente em instantes.");
    }
    setLoading(false);
  };

  const reset = () => {
    setText("");
    setComplaint(null);
    setFrequency(null);
    setWeight("");
  };

  const pill = (active) =>
    `rounded-full px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-colors duration-300 ${
      active ? "bg-military text-offwhite" : "bg-white border border-military/20 text-military-dark/70 hover:bg-baby hover:text-military-dark"
    }`;

  return (
    <section id="dosagem" data-testid="dosage-section" className="bg-white py-24 md:py-32 lg:py-40 relative overflow-hidden">
      <div className="absolute top-20 right-0 w-[30rem] h-[30rem] rounded-full bg-baby/50 blur-3xl pointer-events-none" />
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-4 flex items-center gap-2">
            <Calculator size={14} /> 04 · Calculadora de dosagem
          </p>
          <h2 className="font-serif text-4xl md:text-6xl text-military-dark tracking-tight max-w-3xl">
            Quanto tomar, <span className="italic font-light text-military">exatamente?</span>
          </h2>
          <p className="mt-6 max-w-lg text-base md:text-lg text-military-dark/70 leading-relaxed">
            Conte sua queixa e seu peso — a calculadora monta um protocolo de dosagem diária com os produtos leve essenza.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-military-dark/50 font-semibold mb-3">Sua queixa</p>
            <div className="flex flex-wrap gap-2 mb-10" data-testid="dosage-group-complaint">
              {complaints.map((c) => (
                <button
                  key={c}
                  data-testid={`dosage-option-${c.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setComplaint(c)}
                  className={pill(complaint === c)}
                >
                  {c}
                </button>
              ))}
            </div>

            <p className="text-xs uppercase tracking-[0.25em] text-military-dark/50 font-semibold mb-3">Seu peso (kg)</p>
            <div className="relative mb-10 max-w-xs">
              <Scale size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-military/50" />
              <input
                data-testid="dosage-weight-input"
                type="number"
                min="30"
                max="250"
                placeholder="Ex.: 75"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full bg-white border border-military/20 rounded-full pl-11 pr-16 py-3.5 text-sm text-military-dark placeholder:text-military-dark/40 focus:outline-none focus:border-military transition-colors"
              />
              <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest text-military-dark/40 font-semibold">kg</span>
            </div>

            <p className="text-xs uppercase tracking-[0.25em] text-military-dark/50 font-semibold mb-3">Treinos por semana</p>
            <div className="flex flex-wrap gap-2 mb-10" data-testid="dosage-group-frequency">
              {frequencies.map((f) => (
                <button
                  key={f}
                  data-testid={`dosage-freq-${f.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setFrequency(f)}
                  className={pill(frequency === f)}
                >
                  {f}
                </button>
              ))}
            </div>

            <button
              data-testid="dosage-calculate-button"
              onClick={run}
              disabled={!ready || loading}
              className="rounded-full bg-military-dark text-offwhite px-8 py-4 text-sm uppercase tracking-widest font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-baby hover:text-military-dark transition-colors duration-300 flex items-center gap-2"
            >
              <Calculator size={16} /> {loading ? "Calculando..." : "Calcular minha dosagem"}
            </button>
          </motion.div>

          <div className="lg:col-span-7 flex items-stretch">
            <AnimatePresence mode="wait">
              {text || loading ? (
                <motion.div
                  key="result"
                  data-testid="dosage-result-panel"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="w-full bg-offwhite border border-military/15 rounded-lg p-8 md:p-12 flex flex-col"
                >
                  <p className="text-xs uppercase tracking-[0.25em] text-military font-semibold mb-6">Seu protocolo diário</p>
                  <div className="font-serif text-xl md:text-2xl text-military-dark leading-relaxed whitespace-pre-wrap flex-1">
                    {renderMd(text)}
                    {loading && <span className="inline-block w-2 h-6 bg-military ml-1 animate-pulse align-middle" />}
                  </div>
                  {!loading && text && (
                    <div className="mt-10 flex flex-wrap gap-4">
                      <a
                        href="#catalogo"
                        data-testid="dosage-see-catalog-button"
                        onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#catalogo", { offset: -60, duration: 1.4 }); }}
                        className="rounded-full bg-military text-offwhite px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors duration-300"
                      >
                        Ver produtos
                      </a>
                      <button
                        data-testid="dosage-reset-button"
                        onClick={reset}
                        className="rounded-full border border-military/25 text-military-dark/70 px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-baby hover:text-military-dark transition-colors duration-300 flex items-center gap-2"
                      >
                        <RotateCcw size={14} /> Recalcular
                      </button>
                      <a
                        data-testid="dosage-whatsapp-button"
                        href={`https://wa.me/${WA}?text=${encodeURIComponent(`Olá! Calculei meu protocolo de dosagem no site da leve essenza:\n\n${text}\n\nPodem me ajudar com esses produtos?`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-full bg-military-dark text-offwhite px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-military transition-colors duration-300 flex items-center gap-2"
                      >
                        <MessageCircle size={14} /> Receber no WhatsApp
                      </a>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full border border-dashed border-military/20 rounded-lg p-12 flex flex-col items-center justify-center text-center min-h-[28rem]"
                >
                  <Scale size={32} className="text-military/30 mb-6" />
                  <p className="font-serif italic text-2xl md:text-3xl text-military-dark/40 max-w-sm">
                    Seu protocolo de dosagem será calculado aqui, grama por grama.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
